import { m as motion, useScroll } from 'framer-motion';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Document } from '@/entities/document';
import { Format } from '@/entities/format';
import { Page } from '@/entities/page';
import { Place } from '@/entities/place';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getAccordions } from '@/shared/api/accordions';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getEmployees } from '@/shared/api/employees';
import { getHistory } from '@/shared/api/history';
import { getPageDetail } from '@/shared/api/page-detail';
import { getFormats } from '@/shared/api/participation-formats';
import { getPeople } from '@/shared/api/people';
import { getPlaces } from '@/shared/api/places';
import { getPrivileges } from '@/shared/api/privileges';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getReasons } from '@/shared/api/reasons';
import { getSelectionStages } from '@/shared/api/selection-stages';
import { getSiteSettings } from '@/shared/api/site-settings';
import { getTickets } from '@/shared/api/tickets';
import { getVideo } from '@/shared/api/video';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AboutSection, { AboutSectionProps } from '@/shared/components/sections/AboutSection';
import AwardsSection, { AwardsSectionProps } from '@/shared/components/sections/AwardsSection';
import FullscreenVideoSection, {
    FullscreenVideoSectionProps,
} from '@/shared/components/sections/FullscreenVideoSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import MissionSection, { MissionSectionProps } from '@/shared/components/sections/MissionSection';
import OrganizeSection, { OrganizeSectionProps } from '@/shared/components/sections/OrganizeSection';
import PageWelcome from '@/shared/components/sections/PageWelcome';
import ParticipantsSection, { ParticipantsSectionProps } from '@/shared/components/sections/ParticipantsSection';
import { ProgramDetailSectionProps } from '@/shared/components/sections/ProgramDetailSection/ProgramDetailSection';
import ProgramDetailSeparateSection from '@/shared/components/sections/ProgramDetailSeparateSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import SelectionStagesSection, {
    SelectionStagesSectionProps,
} from '@/shared/components/sections/SelectionStagesSection';
import {
    TariffsInfoSectionWithPopup,
    TariffsInfoSectionWithPopupProps,
} from '@/shared/components/sections/TariffsInfoSection';
import TextWithImageSection, {
    TextWithImageSectionProps,
} from '@/shared/components/sections/TextWithImageSection/TextWithImageSection';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import NavigationSections from '@/shared/components/shared/NavigationSections';
import ScrollTopButton from '@/shared/components/shared/ScrollTopButton';
import Typografed from '@/shared/components/shared/Typografed';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { computeEventDatesFromDays, generateEventPageJsonLd } from '@/shared/lib/utils/event-page-json-ld';
import { buildTariffContacts } from '@/shared/lib/utils/tariff-contacts';

const SUMMIT_SECTION_IDS = {
    about: 'aboutSection',
    howItWas: 'howItWas',
    participants: 'participants',
    mission: 'mission',
    program: 'program-list',
    tariffs: 'tarifs',
    organize: 'organize',
    textWithImage: 'textWithImage',
    awards: 'awards',
    selectionStages: 'selectionStages',
    feedback: 'feedback',
} as const;

const SummitPage = ({
    meta,
    footerData,
    breadcrumbs,
    page,
    h1,
    navigation,
    bodyClass,
    anchors,
    sectionIds,
    aboutSectionProps,
    fullscreenVideoSectionProps,
    howItWasSectionProps,
    participantsSectionProps,
    missionSectionProps,
    tariffsInfoSectionProps,
    organizeSectionProps,
    textWithImageSectionProps,
    awardsSectionProps,
    selectionStagesSectionProps,
    contactPersonProps,
    questionsSectionProps,
    eventStartDateIso,
    eventEndDateIso,
    days,
    initialParams = {},
    initialProgramResponse,
    formats,
    programDetailSectionProps,
    places,
    directions,
    programFields,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });
    return (
        <DefaultLayout currentPageId={PAGES.HR} bodyClass={bodyClass}>
            <section className="hr-summit__section" ref={ref}>
                <PageWelcome breadcrumbs={breadcrumbs} title={h1} />

                <NavigationSections navigation={navigation} className="wrapper" />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <NavigationAnchors
                        theme={theme}
                        navigation={navigation}
                        anchors={anchors}
                        scrollYProgress={scrollYProgress}
                    />
                    <AboutSection id={sectionIds.about} aboutSectionProps={aboutSectionProps} document={page?.file} />
                    <FullscreenVideoSection data={fullscreenVideoSectionProps} data-theme="dark" />
                    {howItWasSectionProps.slider.images.length > 0 && (
                        <HowItWasSection
                            id={sectionIds.howItWas}
                            howItWasSectionProps={howItWasSectionProps}
                            theme="dark"
                        />
                    )}
                    {participantsSectionProps.participantsSlider.length > 0 && (
                        <ParticipantsSection
                            participantsSectionProps={participantsSectionProps}
                            className="js-header-theme-trigger js-bg-trigger-dark"
                            data-bg="#f2f2f2"
                            data-theme="dark"
                            data-gradient-bg="light"
                            id={sectionIds.participants}
                        />
                    )}
                    <MissionSection id={sectionIds.mission} missionSectionProps={missionSectionProps} />
                    {programDetailSectionProps.cards.length > 0 && (
                        <ProgramDetailSeparateSection
                            programDetailSectionProps={programDetailSectionProps}
                            days={days}
                            formats={formats}
                            initialParams={initialParams}
                            initialResponse={initialProgramResponse}
                            places={places}
                            directions={directions}
                            title={programFields.title}
                            titleTag="h2"
                            file={programFields.file}
                            downloadPDF
                        />
                    )}
                    {(tariffsInfoSectionProps.tariffs?.length ?? 0) > 0 && (
                        <TariffsInfoSectionWithPopup {...tariffsInfoSectionProps} id={sectionIds.tariffs} />
                    )}
                    {organizeSectionProps.categories.length > 0 && (
                        <OrganizeSection id={sectionIds.organize} organizeSectionProps={organizeSectionProps} />
                    )}
                    <TextWithImageSection
                        textWithImageSectionProps={textWithImageSectionProps}
                        id={sectionIds.textWithImage}
                    >
                        <div className="summit__text-with-image__content">
                            {textWithImageSectionProps.text && (
                                <div
                                    className="text-with-image__text text-m wysiwyg"
                                    dangerouslySetInnerHTML={{ __html: tp(textWithImageSectionProps.text) }}
                                ></div>
                            )}
                            {textWithImageSectionProps.description && (
                                <div className="text-with-image__block-bottom">
                                    <div className="text-with-image__description text-l">
                                        <Typografed>{textWithImageSectionProps.description}</Typografed>
                                    </div>
                                </div>
                            )}
                        </div>
                    </TextWithImageSection>
                    {awardsSectionProps.awards && awardsSectionProps.awards.length > 0 && (
                        <AwardsSection id={sectionIds.awards} awardsSectionProps={awardsSectionProps} />
                    )}
                    {selectionStagesSectionProps.cards.length > 0 && (
                        <SelectionStagesSection
                            id={sectionIds.selectionStages}
                            selectionStagesSectionProps={selectionStagesSectionProps}
                        />
                    )}
                    <QuestionsSection
                        id={sectionIds.feedback}
                        questionsSectionProps={questionsSectionProps}
                        contactPersonProps={contactPersonProps}
                        data-theme="dark"
                    />
                </motion.div>
            </section>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-summit"
                data={generateEventPageJsonLd({
                    name: aboutSectionProps.title || h1,
                    descriptionHtml: aboutSectionProps.description,
                    metaDescription: meta.description,
                    pagePath: '/events/summit/',
                    imageSrc: textWithImageSectionProps.img?.src ?? null,
                    startDateIso: eventStartDateIso ?? undefined,
                    endDateIso: eventEndDateIso ?? undefined,
                    offers: (tariffsInfoSectionProps.tariffs ?? []).map((tariff) => ({
                        name: tariff.title,
                        price: tariff.price,
                        url: tariff.button?.href,
                    })),
                    breadcrumbs,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default SummitPage;

type EventsPageProps = NonUndefined<
    CommonPageProps & {
        eventStartDateIso: string | null;
        eventEndDateIso: string | null;
        page?: Page;
        h1: string;
        aboutSectionProps: AboutSectionProps;
        fullscreenVideoSectionProps: FullscreenVideoSectionProps;
        howItWasSectionProps: HowItWasSectionProps;
        participantsSectionProps: ParticipantsSectionProps;
        missionSectionProps: MissionSectionProps;
        tariffsInfoSectionProps: TariffsInfoSectionWithPopupProps;
        organizeSectionProps: OrganizeSectionProps;
        textWithImageSectionProps: TextWithImageSectionProps;
        awardsSectionProps: AwardsSectionProps;
        selectionStagesSectionProps: SelectionStagesSectionProps;
        navigation: AppNavigation;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        anchors: Anchors;
        sectionIds: typeof SUMMIT_SECTION_IDS;
        programDetailSectionProps: ProgramDetailSectionProps;
        days: Day[];
        formats: Format[];
        initialParams: ApiProgramRequest;
        initialProgramResponse: AppProgramResponse;
        places: Place[];
        directions: Direction[];
        programFields: {
            title: string;
            file: Document | null;
        };
    }
>;

export const getServerSideProps: GetServerSideProps<EventsPageProps> = async ({ query }) => {
    const pageResponse = await getPageDetail({ params: { slug: 'hr' } });

    const initialParams: ApiProgramRequest = {};

    const daysResponse = await getDays({ params: { page: pageResponse.data?.id ?? PAGES.HR } });
    const directionsResponse = await getDirections({ params: { from_hr: 1 } });
    const directions = directionsResponse.data || [];

    const days = daysResponse.data || [];
    const dayIds = days.map((day) => day.id);

    const hrProgramResponse = await getProgram({
        params: {
            days: dayIds,
            directions: directions.map((direction) => direction.id),
        },
    });

    initialParams.days = dayIds;
    initialParams.directions = directions.map((direction) => direction.id);

    if (typeof query.day === 'string') {
        initialParams.day = parseInt(query.day);
    }

    if (typeof query.directions === 'string') {
        initialParams.directions = query.directions.split(',').map((id) => parseInt(id.trim()));
    }

    if (typeof query.q === 'string') {
        initialParams.q = query.q;
    }

    const companyCategoriesResponse = await getCompanyCategories({
        params: { page: pageResponse.data?.id ?? PAGES.HR },
    });
    const [
        commonPageProps,
        companiesResponse,
        peopleResponse,
        historyResponse,
        accordionsResponse,
        selectionStagesResponse,
        employeesResponse,
        videoResponse,
        reasonsResponse,
        formatsResponse,
        placesResponse,
        privilegesResponse,
        ticketsResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.HR),
        companyCategoriesResponse.data?.length === 0
            ? Promise.resolve<AppCompaniesResponse>({ status: 'success', data: [] })
            : getCompanies({
                  params: { categories: (companyCategoriesResponse.data ?? []).map((category) => category.id) },
              }),
        getPeople({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getHistory({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getAccordions({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getSelectionStages({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getVideo({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getReasons({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getFormats({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getPlaces(),
        getPrivileges({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getTickets({ params: { page: pageResponse.data?.id ?? PAGES.HR } }),
        getSiteSettings({
            params: {
                keys: [
                    'hr_first_screen_title',
                    'hr_first_screen_description',

                    'hr_history_title',
                    'hr_history_description',
                    'hr_history_link_text',
                    'hr_history_link',

                    'hr_people_title',

                    'hr_participate_title',
                    'hr_participate_description',
                    'hr_participate_link_text',
                    'hr_participate_link',

                    'hr_program_title',
                    'hr_program_document',

                    'hr_tariff_title',
                    'hr_tariff_description',
                    'hr_tariff_link_text',
                    'hr_tariff_link',
                    'hr_tariff_discount',
                    'hr_tariff_contact_title',
                    'hr_tariff_contact_description',
                    'hr_tariff_contact_phone',
                    'hr_tariff_contact_email',

                    'hr_company_title',

                    'hr_about_title',
                    'hr_about_subtitle',
                    'hr_about_description',
                    'hr_about_image',

                    'hr_nominations_title',

                    'hr_selection_stages_title',
                    'hr_selection_stages_documents',

                    'hr_form_title',
                    'hr_form_description',

                    'hr_tariff_offer_document',
                    'hr_tariff_disclaimer',
                    'hr_tariff_discount_price_label',
                    'hr_tariff_future_price_label',
                ],
            },
        }),
    ]);

    const { startDateIso: eventStartDateIso, endDateIso: eventEndDateIso } = computeEventDatesFromDays(days);

    const formats = formatsResponse.data || [];
    const places = placesResponse.data || [];
    const programFields = {
        title: siteSettings.data?.hr_program_title ?? 'Программа',
        file: siteSettings.data?.hr_program_document ?? null,
    };

    const summitAnchors: Anchors = [
        {
            id: SUMMIT_SECTION_IDS.about,
            name: 'О HR-саммите',
        },
        ...((historyResponse.data?.images ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.howItWas,
                      name: 'Как было в том году?',
                  },
              ]
            : []),
        ...((peopleResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.participants,
                      name: 'Участники',
                  },
              ]
            : []),
        ...((reasonsResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.mission,
                      name: 'Зачем участвовать?',
                  },
              ]
            : []),
        ...((hrProgramResponse.data?.items ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.program,
                      name: 'Программа',
                  },
              ]
            : []),
        ...((ticketsResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.tariffs,
                      name: 'Тарифы',
                  },
              ]
            : []),
        ...((companyCategoriesResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.organize,
                      name: 'Организаторы и партнеры',
                  },
              ]
            : []),
        ...(siteSettings.data?.hr_about_image
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.textWithImage,
                      name: 'HR-премия',
                  },
              ]
            : []),
        ...((accordionsResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.awards,
                      name: 'Номинации HR-Премии',
                  },
              ]
            : []),
        ...((selectionStagesResponse.data ?? []).length > 0
            ? [
                  {
                      id: SUMMIT_SECTION_IDS.selectionStages,
                      name: 'Этапы отбора',
                  },
              ]
            : []),
        {
            id: SUMMIT_SECTION_IDS.feedback,
            name: 'Обратная связь',
        },
    ];

    return {
        props: {
            ...commonPageProps,
            eventStartDateIso: eventStartDateIso ?? null,
            eventEndDateIso: eventEndDateIso ?? null,
            bodyClass: 'summit-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'События форума', href: '/events/summit/' },
            ],
            page: pageResponse.data,
            h1: 'События форума',

            navigation: [
                {
                    id: 0,
                    title: 'Премия Территория технологий',
                    href: '/events/premiya-tec/',
                },
                {
                    id: 1,
                    title: 'Технологические дни',
                    href: '/events/technological/',
                },
                {
                    id: 2,
                    title: 'HR-Саммит и Премия',
                    href: '/events/summit/',
                },
                {
                    id: 3,
                    title: 'TNF LIVE',
                    href: '/events/tnf-live/',
                },
            ],
            anchors: summitAnchors,
            sectionIds: SUMMIT_SECTION_IDS,
            aboutSectionProps: {
                title: siteSettings.data?.hr_first_screen_title ?? 'О HR-саммите',
                description:
                    siteSettings.data?.hr_first_screen_description ??
                    `
                    <p>Фокус на человекоцентричность, наращивание инвестиций в людей — одно из обязательных условий технологической устойчивости компаний в сфере нефтегазовой промышленности и ТЭК. Широкий круг вопросов, связанных с эффективным использованием трудового ресурса, требует решения с учетом современных реалий.</p>
                    <p><br/> III HR-саммит TNF — это площадка для экспертных дискуссий, обогащение отраслевых HR-практик кросс-индустриальным опытом, выработки совместных решений по развитию человеческого капитала как внутри отдельных отраслей, так и на уровне регионов и страны в целом.</p>
                    <p><br/> Это профильное мероприятие для HRD крупных промышленных компаний. Возможность обсудить с коллегами единомышленниками актуальную проблематику обеспеченности бизнеса квалифицированными кадрами, воспользоваться эффектом синергии и найти лучшее HR-решение для своего предприятия.</p>
                `,
            },
            fullscreenVideoSectionProps: {
                video: videoResponse.data?.video ?? [],
                mobileVideo: videoResponse.data?.mobileVideo ?? [],
            },
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: siteSettings.data?.hr_history_title ?? 'Как было в том году?',
                description:
                    siteSettings.data?.hr_history_description ??
                    'Форум прошлого года стал одним из самых значимых событий нефтегазовой промышленности, объединив в Тюмени ведущих экспертов и ключевых игроков отрасли со всей страны',
                link: siteSettings.data?.hr_history_link
                    ? {
                          href: siteSettings.data?.hr_history_link,
                          name: siteSettings.data?.hr_history_link_text ?? '',
                      }
                    : null,
            },
            participantsSectionProps: {
                title: siteSettings.data?.hr_people_title ?? 'Программный комитет и спикеры',
                participantsSlider: (peopleResponse.data ?? []).map((person) => ({
                    name: person.name,
                    job: person.job,
                    img: person.img,
                })),
            },
            missionSectionProps: {
                title: siteSettings.data?.hr_participate_title ?? 'Зачем участвовать?',
                subtitle: siteSettings.data?.hr_participate_description ?? 'Для участия необходимо пройти регистрацию.',
                link: siteSettings.data?.hr_participate_link
                    ? {
                          name: siteSettings.data?.hr_participate_link_text ?? 'Подать заявку',
                          href: siteSettings.data?.hr_participate_link ?? 'https://lk.oilgasforum.ru/auth/',
                      }
                    : null,
                cards: reasonsResponse.data ?? [],
            },
            programDetailSectionProps: {
                cards: hrProgramResponse.data?.items || [],
            },
            days,
            initialParams,
            formats,
            places,
            directions,
            initialProgramResponse: hrProgramResponse,
            programFields: programFields,

            tariffsInfoSectionProps: {
                title: siteSettings.data?.hr_tariff_title ?? 'Тарифы',
                subtitle:
                    siteSettings.data?.hr_tariff_description ??
                    'Для покупки зарегистрируйтесь или войдите в личный кабинет',
                download: siteSettings.data?.hr_tariff_offer_document?.src
                    ? {
                          href: siteSettings.data.hr_tariff_offer_document.src,
                          text:
                              siteSettings.data.hr_tariff_offer_document.title ??
                              'Лицензионный договор - оферта HR-саммита',
                      }
                    : null,
                popupTitle: siteSettings.data?.hr_tariff_title ?? 'Тарифы',
                allTariffFeatures: privilegesResponse.data ?? [],
                tariffs: (ticketsResponse.data ?? []).map((ticket) => ({
                    id: ticket.id,
                    title: ticket.title,
                    text: ticket.description ?? '',
                    color: ticket.color || null,
                    price: ticket.price ?? '',
                    priceNote: siteSettings.data?.hr_tariff_disclaimer ?? '',
                    button: {
                        text: ticket.button?.text || siteSettings.data?.hr_tariff_link_text || 'Подать заявку',
                        href: ticket.button?.href || siteSettings.data?.hr_tariff_link || '/registration/',
                        isExternal: ticket.button?.isExternal || false,
                    },
                    features: ticket.features ?? [],
                    futurePrice: ticket.futurePrice ?? '',
                })),
                discounts: (ticketsResponse.data ?? [])
                    .filter((ticket) => ticket.price)
                    .map((ticket) => ({
                        tariffId: ticket.id,
                        price: ticket.price ?? '',
                    })),
                discountPriceLabel: siteSettings.data?.hr_tariff_discount_price_label || null,
                futurePriceLabel: siteSettings.data?.hr_tariff_future_price_label || null,
                notes: [
                    '* Кроме мероприятий по приглашениям',
                    '** Запись через личный кабинет, при наличии свободных мест',
                ],
                contacts: buildTariffContacts({
                    title: siteSettings.data?.hr_tariff_contact_title,
                    description: siteSettings.data?.hr_tariff_contact_description,
                    phone: siteSettings.data?.hr_tariff_contact_phone,
                    email: siteSettings.data?.hr_tariff_contact_email,
                }),
            } satisfies TariffsInfoSectionWithPopupProps,

            organizeSectionProps: {
                title: siteSettings.data?.hr_company_title ?? 'Организаторы и партнеры',
                categories: (companyCategoriesResponse.data ?? []).map((category) => ({
                    title: category.name,
                    description: category.description ?? '',
                    items: (companiesResponse.data ?? [])
                        .filter((company) => company.categoryId === category.id)
                        .map((company) => ({
                            logo: company.logo,
                            title: company.title,
                            description: company.description ?? '',
                            href: company.href ?? '',
                            categoryId: company.categoryId,
                        })),
                })),
            },
            textWithImageSectionProps: {
                title: siteSettings.data?.hr_about_title ?? 'Получите премию «HR-проект года в промышленности»',
                text:
                    siteSettings.data?.hr_about_description ??
                    `
                <p>Первая межотраслевая профессиональная премия в сфере управления персоналом лидеров промышленности и ТЭК.</p>
                <p>Награды достанутся проектам, которые принесли наиболее значимые результаты в сфере промышленности как бизнесу, так и сотрудникам.</p>
                `,
                description: siteSettings.data?.hr_about_subtitle ?? 'Бесплатно для компаний-участников HR-саммита TNF',
                img: siteSettings.data?.hr_about_image ?? null,
            },
            awardsSectionProps: {
                title: siteSettings.data?.hr_nominations_title ?? 'Номинации HR-Премии',
                awards: accordionsResponse.data ?? [],
            },
            selectionStagesSectionProps: {
                title: siteSettings.data?.hr_selection_stages_title ?? 'Этапы отбора',
                documents: (siteSettings.data?.hr_selection_stages_documents ?? []).map((document) => ({
                    src: document.src,
                    title: document.title ?? '',
                    alt: document.alt ?? '',
                })),
                cards: selectionStagesResponse.data ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.hr_form_title ?? 'Остались вопросы? Напишите нам',
                description:
                    siteSettings.data?.hr_form_description ??
                    'Планируете участие в выставке?<br>Напишите нам, и мы с вами свяжемся',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
        } satisfies EventsPageProps,
    };
};
